import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate, useLogin, useNotify, useSafeSetState } from 'ra-core';
import { Eye, EyeOff } from 'lucide-react';

const useStyles = makeStyles(
  (theme) => ({
    form: {
      padding: '0 2em 2em 2em',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2.5),
    },
    inputWrapper: {
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      backgroundColor: 'rgba(158, 201, 194, 0.15)',
    },
    inputContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: '100%',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      padding: '16px',
      fontSize: '14px',
      color: theme.palette.text.primary,
      '&::placeholder': {
        color: theme.palette.text.secondary,
      },
    },
    passwordToggle: {
      position: 'absolute',
      right: '12px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      padding: '4px',
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.text.primary,
      },
    },
    label: {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      marginBottom: '8px',
      display: 'block',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
    },
    rememberMeContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '14px',
    },
    rememberMeLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      color: theme.palette.text.primary,
      '& .MuiCheckbox-root': {
        padding: '4px',
      },
    },
    resetPasswordLink: {
      color: '#538181',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    submitButton: {
      width: '100%',
      borderRadius: '16px',
      padding: '16px',
      fontSize: '16px',
      fontWeight: 500,
      textTransform: 'none',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
      '&:disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
      },
    },
    divider: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: theme.spacing(2, 0),
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        height: '1px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    dividerText: {
      position: 'relative',
      padding: '0 16px',
      fontSize: '14px',
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.default,
    },
    googleButton: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      borderRadius: '16px',
      padding: '16px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      fontSize: '16px',
      fontWeight: 500,
      textTransform: 'none',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
      },
    },
    createAccountText: {
      textAlign: 'center',
      fontSize: '14px',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(1),
    },
    createAccountLink: {
      color: '#538181',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    errorText: {
      color: theme.palette.error.main,
      fontSize: '12px',
      marginTop: '4px',
    },
  }),
  { name: 'SignInForm' }
);

const GlassInputWrapper = ({ children, hasError }) => {
  const classes = useStyles();
  return (
    <div className={classes.inputWrapper} style={hasError ? { borderColor: 'rgba(244, 67, 54, 0.7)' } : {}}>
      {children}
    </div>
  );
};

GlassInputWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  hasError: PropTypes.bool,
};

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s12-5.373 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-2.641-.21-5.236-.611-7.743z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.022 35.026 44 30.038 44 24c0-2.641-.21-5.236-.611-7.743z" />
  </svg>
);

const SignInForm = ({
  onSignIn,
  onResetPassword,
  onCreateAccount,
  onGoogleSignIn,
  redirectTo,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const classes = useStyles();
  const isMountedRef = useRef(true);

  // Hooks ra-core (utilisés quand onSignIn n'est pas fourni)
  const login = useLogin();
  const translate = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useSafeSetState(false);

  // Nettoyer le ref quand le composant est démonté
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    const formData = new FormData(event.currentTarget);
    const email = formData.get('email');
    const password = formData.get('password');

    // Validation
    const newErrors = {};
    if (!email) {
      newErrors.email = translate('ra.validation.required') || 'Email is required';
    }
    if (!password) {
      newErrors.password = translate('ra.validation.required') || 'Password is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      if (onSignIn) {
        // Passer l'événement directement pour que l'utilisateur puisse utiliser event.currentTarget
        await onSignIn(event);
      } else {
        // Utiliser les hooks ra-core
        await login({ username: email, password }, redirectTo);
      }
    } catch (error) {
      console.error('Sign in error:', error);
      if (isMountedRef.current) {
        if (onSignIn) {
          setErrors({ submit: error.message || 'An error occurred' });
        } else {
          notify(
            typeof error === 'string'
              ? error
              : typeof error === 'undefined' || !error.message
              ? 'ra.auth.sign_in_error'
              : error.message,
            'warning'
          );
        }
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    if (onResetPassword) {
      onResetPassword();
    }
  };

  const handleGoogleSignIn = () => {
    if (onGoogleSignIn) {
      onGoogleSignIn();
    } else {
      console.log('Google Sign In clicked');
    }
  };

  const handleCreateAccount = (e) => {
    e.preventDefault();
    if (onCreateAccount) {
      onCreateAccount();
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className={classes.form}>
        <div className={classes.inputGroup}>
          <label className={classes.label} htmlFor="email">
            {translate('ra.auth.username') || 'Email Address'}
          </label>
          <GlassInputWrapper hasError={!!errors.email}>
            <div className={classes.inputContainer}>
              <input
                id="email"
                name="email"
                type="email"
                placeholder={translate('ra.auth.username') || 'Enter your email address'}
                className={classes.input}
                disabled={loading}
                autoFocus
                required
              />
            </div>
          </GlassInputWrapper>
          {errors.email && <span className={classes.errorText}>{errors.email}</span>}
        </div>

        <div className={classes.inputGroup}>
          <label className={classes.label} htmlFor="password">
            {translate('ra.auth.password') || 'Password'}
          </label>
          <GlassInputWrapper hasError={!!errors.password}>
            <div className={classes.inputContainer}>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={translate('ra.auth.password') || 'Enter your password'}
                className={classes.input}
                disabled={loading}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={classes.passwordToggle}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </GlassInputWrapper>
          {errors.password && <span className={classes.errorText}>{errors.password}</span>}
        </div>

        <div className={classes.rememberMeContainer}>
          <FormControlLabel
            control={<Checkbox name="rememberMe" style={{ color: 'var(--color-teal)' }} />}
            label="Keep me signed in"
            className={classes.rememberMeLabel}
          />
          <Link
            href="#"
            onClick={handleResetPassword}
            className={classes.resetPasswordLink}
          >
            Reset password
          </Link>
        </div>

        {errors.submit && <span className={classes.errorText}>{errors.submit}</span>}

        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          className={classes.submitButton}
          style={{ backgroundColor: 'var(--color-teal)' }}
        >
          {loading && <CircularProgress className={classes.icon} size={18} thickness={2} />}
          {translate('ra.auth.sign_in') || 'Sign In'}
        </Button>

        {/* {(onGoogleSignIn || !onSignIn) && (
          <>
            <div className={classes.divider}>
              <span className={classes.dividerText}>Or continue with</span>
            </div>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className={classes.googleButton}
            >
              <GoogleIcon />
              Continue with Google
            </button>
          </>
        )} */}

        {/* {onCreateAccount && (
          <p className={classes.createAccountText}>
            New to our platform?{' '}
            <Link
              href="#"
              onClick={handleCreateAccount}
              className={classes.createAccountLink}
            >
              Create Account
            </Link>
          </p>
        )} */}
      </div>
    </form>
  );
};

SignInForm.propTypes = {
  onSignIn: PropTypes.func,
  onResetPassword: PropTypes.func,
  onCreateAccount: PropTypes.func,
  onGoogleSignIn: PropTypes.func,
  redirectTo: PropTypes.string,
};

export default SignInForm;

